<template>
  <Teleport to="body">
    <div id="toast-root" class="toast-container" aria-live="polite">
      <TransitionGroup name="toast-list" tag="div" class="toast-stack">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          class="custom-toast"
          :class="`custom-toast--${toast.type}`"
          @mouseenter="pauseToast(toast.id)"
          @mouseleave="resumeToast(toast.id)"
        >
          <div class="toast-body">
            <div class="toast-icon" aria-hidden="true">
              <svg v-if="toast.type === 'success'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><path d="M22 11.08V12a10 10 0 1 1-5.93-8.93"/><polyline points="22 4 12 14 9 11"/></svg>
              <svg v-else-if="toast.type === 'error'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
              <svg v-else-if="toast.type === 'warning'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
            </div>
            <p class="toast-message">{{ toast.message }}</p>
            <button type="button" class="toast-close-button" aria-label="Fechar" @click="removeToast(toast.id)">×</button>
          </div>
          <div class="toast-progress-track" aria-hidden="true">
            <div
              class="toast-progress-bar"
              :style="{
                '--toast-duration': `${toast.duration}ms`,
                'animation-play-state': toast.paused ? 'paused' : 'running',
              }"
            />
          </div>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script>
import { reactive, onMounted } from 'vue'

const toastStore = reactive({
  toasts: [],
  toastIdCounter: 0,
})

const timers = new Map()

const removeToast = (id) => {
  const timer = timers.get(id)
  if (timer) {
    clearTimeout(timer.timeoutId)
    timers.delete(id)
  }
  const index = toastStore.toasts.findIndex(t => t.id === id)
  if (index > -1) toastStore.toasts.splice(index, 1)
}

const scheduleRemove = (id, delay) => {
  const prev = timers.get(id)
  if (prev) clearTimeout(prev.timeoutId)
  const timeoutId = setTimeout(() => removeToast(id), delay)
  timers.set(id, { timeoutId, remaining: delay, startedAt: Date.now() })
}

const pauseToast = (id) => {
  const toast = toastStore.toasts.find(t => t.id === id)
  const timer = timers.get(id)
  if (!toast || !timer || toast.paused) return
  const elapsed = Date.now() - timer.startedAt
  timer.remaining = Math.max(0, timer.remaining - elapsed)
  clearTimeout(timer.timeoutId)
  toast.paused = true
}

const resumeToast = (id) => {
  const toast = toastStore.toasts.find(t => t.id === id)
  const timer = timers.get(id)
  if (!toast || !timer || !toast.paused) return
  toast.paused = false
  scheduleRemove(id, timer.remaining)
}

const showToast = (message, type = 'info', duration = 3500) => {
  const text = String(message || '').trim()
  if (!text) return null
  const id = ++toastStore.toastIdCounter
  toastStore.toasts.push({
    id,
    message: text,
    type,
    duration: duration || 3500,
    paused: false,
  })
  if ((duration || 3500) > 0) {
    scheduleRemove(id, duration || 3500)
  }
  return id
}

const bindGlobal = () => {
  if (typeof window === 'undefined') return
  window.showToast = {
    success: (message, duration) => showToast(message, 'success', duration ?? 3000),
    error: (message, duration) => showToast(message, 'error', duration ?? 4500),
    warning: (message, duration) => showToast(message, 'warning', duration ?? 4000),
    info: (message, duration) => showToast(message, 'info', duration ?? 3500),
  }
  if (!window.__ralphAlertPatched) {
    window.__ralphAlertPatched = true
    window.alert = (msg) => {
      window.showToast.warning(String(msg), 4500)
    }
  }
}

bindGlobal()

export default {
  name: 'Toast',
  setup() {
    onMounted(() => bindGlobal())
    return {
      toasts: toastStore.toasts,
      removeToast,
      pauseToast,
      resumeToast,
    }
  },
}
</script>

<style>
.toast-container {
  position: fixed;
  top: max(12px, env(safe-area-inset-top, 0px));
  left: 12px;
  right: 12px;
  bottom: auto;
  transform: none;
  z-index: 2147483646;
  pointer-events: none;
  width: auto;
  max-width: 440px;
  margin-inline: auto;
  box-sizing: border-box;
}

body.is-iframe .toast-container,
#app.is-iframe ~ .toast-container {
  top: max(56px, calc(48px + env(safe-area-inset-top, 0px)));
}

.toast-stack {
  display: flex;
  flex-direction: column-reverse;
  gap: 10px;
}

.custom-toast {
  pointer-events: auto;
  overflow: hidden;
  border-radius: 14px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.32);
  color: #fff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  min-width: 0;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
}

.custom-toast--success { background: linear-gradient(135deg, #16a34a 0%, #15803d 100%); }
.custom-toast--error { background: linear-gradient(135deg, #ef4444 0%, #b91c1c 100%); }
.custom-toast--warning { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); }
.custom-toast--info { background: linear-gradient(135deg, #3A94A3 0%, #124E57 100%); }

.toast-body {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 12px 10px;
}

.toast-icon {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  margin-top: 1px;
}

.toast-icon svg {
  width: 100%;
  height: 100%;
  stroke: #fff;
}

.toast-message {
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.toast-close-button {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.9);
  font-size: 22px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  padding: 0;
}

.toast-progress-track {
  height: 5px;
  background: rgba(0, 0, 0, 0.22);
}

.toast-progress-bar {
  height: 100%;
  width: 100%;
  background: rgba(255, 255, 255, 0.95);
  transform-origin: left center;
  animation: toast-shrink var(--toast-duration, 3500ms) linear forwards;
}

@keyframes toast-shrink {
  from { transform: scaleX(1); }
  to { transform: scaleX(0); }
}

.toast-list-enter-active,
.toast-list-leave-active {
  transition: all 0.28s ease;
}

.toast-list-enter-from,
.toast-list-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

.toast-list-move {
  transition: transform 0.28s ease;
}

@media (max-width: 640px) {
  .toast-container {
    top: max(8px, env(safe-area-inset-top, 0px));
    left: 8px;
    right: 8px;
    max-width: none;
  }

  body.is-iframe .toast-container,
  #app.is-iframe ~ .toast-container {
    top: max(50px, calc(44px + env(safe-area-inset-top, 0px)));
  }

  .toast-message {
    font-size: 13px;
  }

  .toast-list-enter-from,
  .toast-list-leave-to {
    transform: translateY(-10px);
  }
}
</style>
